import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { commonStyles } from "../pdf/CommonPdfStyles";
import { useTranslation } from "react-i18next";
import { ClassroomManagementResponseDto } from "@packages/api/madlenAPI.schemas";
import { PdfLayout } from "../pdf/PdfLayout";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";

Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

const styles = StyleSheet.create({
  strategyContainer: {
    marginBottom: 12,
    padding: 10,
    backgroundColor: "#fdf4ff",
    borderRadius: 6,
  },
  strategyTitle: {
    fontSize: 12,
    fontFamily: "Montserrat",
    fontWeight: "bold",
    color: "#c026d3",
    marginBottom: 8,
  },

  problemDescContainer: {
    marginBottom: 12,
    padding: 10,
    backgroundColor: "#fef7ff",
    borderRadius: 6,
  },
  problemDescTitle: {
    fontSize: 11,
    fontFamily: "Montserrat",
    fontWeight: "bold",
    color: "#c026d3",
    marginBottom: 6,
  },
  detail: {
    fontSize: 10,
    fontFamily: "Montserrat",
    color: "#374151",
    lineHeight: 1.4,
  },
});

const simplifyMarkdown = (content: string | undefined): string => {
  if (!content) return "";
  return content
    .replace(/#{1,6}\s/g, "")
    .replace(/\*\*/g, "")
    .replace(/\*/g, "")
    .replace(/`/g, "")
    .replace(/\[(.*?)\]\(.*?\)/g, "$1")
    .trim();
};

interface ClassroomManagementCardDocumentProps {
  tool: ClassroomManagementResponseDto;
  customizationSettings: PdfCustomizationSettings;
}

const ClassroomManagementCardDocument = ({
  tool,
  customizationSettings,
}: ClassroomManagementCardDocumentProps) => {
  const { t } = useTranslation();

  const markdownStyle = {
    text: {
      text: {
        ...commonStyles.sectionContent,
        fontSize: scaleFontSize(
          commonStyles.sectionContent.fontSize,
          customizationSettings?.fontSize,
        ),
      },
    },
  };

  const sectionTitleStyle = {
    ...commonStyles.sectionTitle,
    fontSize: scaleFontSize(
      commonStyles.sectionTitle.fontSize,
      customizationSettings?.fontSize,
    ),
  };

  return (
    <PdfLayout
      courseName={tool.courseName ?? ""}
      gradeLevel={tool.gradeLevel?.toString() ?? ""}
      topic={tool.title ?? ""}
      imageUrl={tool.imageUrl ?? ""}
      headerTextColor="#c026d3"
      customizationSettings={customizationSettings}
      author={tool.authorName ?? ""}
      createdDate={tool.createdAt ?? ""}
    >
      {/* Problem Description */}
      {tool.problemDescription && (
        <View wrap={false} style={styles.problemDescContainer}>
          <Text
            style={[
              styles.problemDescTitle,
              {
                fontSize: scaleFontSize(
                  styles.problemDescTitle.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
            ]}
          >
            {t("classroomManagement.problemDescription")}
          </Text>
          <MarkdownRenderer
            customStyle={{
              text: {
                text: {
                  ...styles.detail,
                  fontSize: scaleFontSize(
                    styles.detail.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
              },
            }}
            content={tool.problemDescription}
          />
        </View>
      )}

      {/* Strategies */}
      {tool.strategies?.map((strategy, index) => (
        <View wrap={false} style={styles.strategyContainer} key={index}>
          <Text
            style={[
              styles.strategyTitle,
              {
                fontSize: scaleFontSize(
                  styles.strategyTitle.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
            ]}
          >
            {simplifyMarkdown(strategy.title)}
          </Text>

          {strategy.description && (
            <MarkdownRenderer
              customStyle={{
                text: {
                  text: {
                    ...styles.detail,
                    fontSize: scaleFontSize(
                      styles.detail.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                },
              }}
              content={strategy.description}
            />
          )}

          {strategy.implementation_steps &&
            strategy.implementation_steps.length > 0 && (
              <>
                <Text style={sectionTitleStyle}>
                  {t("classroomManagement.implementationSteps")}
                </Text>
                {strategy.implementation_steps.map((step, stepIndex) => (
                  <MarkdownRenderer
                    key={stepIndex}
                    customStyle={markdownStyle}
                    content={`• ${step}`}
                  />
                ))}
              </>
            )}

          {strategy.additional_suggestions &&
            strategy.additional_suggestions.length > 0 && (
              <>
                <Text style={sectionTitleStyle}>
                  {t("classroomManagement.additionalSuggestions")}
                </Text>
                {strategy.additional_suggestions.map((suggestion, sugIndex) => (
                  <MarkdownRenderer
                    key={sugIndex}
                    customStyle={markdownStyle}
                    content={`• ${suggestion}`}
                  />
                ))}
              </>
            )}

          {strategy.parent_communication_tips &&
            strategy.parent_communication_tips.length > 0 && (
              <>
                <Text style={sectionTitleStyle}>
                  {t("classroomManagement.parentCommunicationTips")}
                </Text>
                {strategy.parent_communication_tips.map((tip, tipIndex) => (
                  <MarkdownRenderer
                    key={tipIndex}
                    customStyle={markdownStyle}
                    content={`• ${tip}`}
                  />
                ))}
              </>
            )}
        </View>
      ))}
    </PdfLayout>
  );
};

const ClassroomManagementCardPDF = ({
  tool,
  customizationSettings,
}: ClassroomManagementCardDocumentProps) => {
  return (
    <ClassroomManagementCardDocument
      tool={tool}
      customizationSettings={customizationSettings}
    />
  );
};

export default ClassroomManagementCardPDF;
